import { useEffect, useState } from "react";

import { useDispatch } from "react-redux";

import { Card, RadioButton, Textarea } from "@stellar/design-system";

import { InfoTooltip } from "@/components/InfoTooltip";

import { getOrgInfoAction } from "@/store/ducks/organization";

import { getDefaultInviteMessage } from "@/helpers/inviteMessageTemplate";

import { useRedux } from "@/hooks/useRedux";

import { AppDispatch } from "@/store";

import "../ReceiverInviteMessage/styles.scss";

interface DisbursementInviteMessageProps {
  draftMessage?: string;
  disabledReasonForTooltip?: string;
  isEditMessage: boolean;
  /** Disbursement language code — drives the default (non-custom) invite copy. */
  language?: string;
  /** Wallet name substituted into the default invite copy. */
  walletName?: string;
  onChange?: (message: string) => void;
}

enum radioValue {
  ORGANIZATION = "organization",
  CUSTOM = "custom",
}

export const DisbursementInviteMessage = ({
  draftMessage,
  isEditMessage,
  disabledReasonForTooltip,
  language,
  walletName,
  onChange,
}: DisbursementInviteMessageProps) => {
  const customMessagePlaceholder =
    "Input your registration invitation message for this disbursement here in the appropriate language. Make sure to say which organization the transaction is coming from. Keep your message short. A link to the wallet will be appended at the end.";

  const { organization } = useRedux("organization");
  const dispatch: AppDispatch = useDispatch();

  const [selectedOption, setSelectedOption] = useState(
    draftMessage?.trim() ? radioValue.CUSTOM : radioValue.ORGANIZATION,
  );
  const [customMessageInput, setCustomMessageInput] = useState(draftMessage?.trim() ?? "");

  useEffect(() => {
    dispatch(getOrgInfoAction());
  }, [dispatch]);

  // The default copy the receivers get when no custom message is set: the
  // organization-wide template if one was configured, otherwise the translation for the
  // disbursement's language. This mirrors what the backend renders when it sends the
  // invitation, so the preview does not stay in English for a non-English disbursement.
  const defaultMessage =
    organization.data.receiverRegistrationMessageTemplate?.trim() ||
    getDefaultInviteMessage({
      language,
      walletName,
      languageTemplates: organization.data.receiverInvitationMessageTemplates,
    });

  const handleOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value === radioValue.ORGANIZATION) {
      updateMessage("");
      setSelectedOption(radioValue.ORGANIZATION);
    } else {
      setSelectedOption(radioValue.CUSTOM);
      updateMessage(customMessageInput);
    }
  };

  const updateMessage = (updatedDisbursementInviteMessage: string) => {
    setCustomMessageInput(updatedDisbursementInviteMessage);
    // Updating parent
    if (onChange) {
      onChange(updatedDisbursementInviteMessage);
    }
  };

  return (
    <>
      <Card>
        <div className="CardStack__card ReceiverInviteMessage">
          <InfoTooltip infoText={disabledReasonForTooltip} hideTooltip={!disabledReasonForTooltip}>
            <div className="CardStack__title">Customize Invite</div>
          </InfoTooltip>

          <div className="Note">
            You can use the organization default message or customized text to invite your receivers
            to set up a wallet and receive funds for this disbursement. It contains a link to the
            appropriate wallet at the end of the message. Please check all values for accuracy.
          </div>

          {isEditMessage && (
            <div className="ReceiverInviteMessage__options">
              <RadioButton
                id="msg-std"
                name="disbursement-message"
                label="Default message"
                fieldSize="sm"
                value={radioValue.ORGANIZATION}
                onChange={handleOptionChange}
                disabled={!!disabledReasonForTooltip}
                checked={selectedOption === radioValue.ORGANIZATION}
              />
              <RadioButton
                id="msg-cst"
                name="disbursement-message"
                label="Custom message"
                fieldSize="sm"
                value={radioValue.CUSTOM}
                onChange={handleOptionChange}
                disabled={!!disabledReasonForTooltip}
                checked={selectedOption === radioValue.CUSTOM}
              />
            </div>
          )}
          {selectedOption === radioValue.CUSTOM ? (
            <div className="ReceiverInviteMessage__form">
              <Textarea
                fieldSize="sm"
                id="textarea-custom-input"
                rows={5}
                placeholder={customMessagePlaceholder}
                value={customMessageInput}
                disabled={!isEditMessage || !!disabledReasonForTooltip}
                onChange={(event) => {
                  updateMessage(event.target.value);
                }}
              ></Textarea>
            </div>
          ) : (
            <div className="ReceiverInviteMessage__form">
              <Textarea
                fieldSize="sm"
                id="textarea-standard"
                disabled
                rows={5}
                value={isEditMessage ? defaultMessage : draftMessage?.trim() || defaultMessage}
              ></Textarea>
            </div>
          )}
        </div>
      </Card>
    </>
  );
};
